import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, Loading, StatusBadge } from "@/components/common";
import { FormDialog } from "@/components/FormDialog";
import { api, useFetch, errMsg, money, CURRENCIES } from "@/lib/api";

const FIELDS = [
  { name: "name", label: "Plan name", required: true },
  { name: "price_monthly", label: "Monthly price (0 = free)", type: "number", required: true },
  { name: "currency", label: "Currency", type: "select", default: "GBP", options: CURRENCIES.map((c) => ({ value: c, label: c })) },
  { name: "max_properties", label: "Max properties (0 = unlimited)", type: "number", step: "1" },
  { name: "description", label: "Short description", full: true },
  { name: "features", label: "Features (one per line)", type: "textarea", rows: 5 },
  { name: "sort", label: "Display order", type: "number", step: "1" },
  { name: "active", label: "Visible & purchasable", type: "checkbox", default: true },
  { name: "is_popular", label: "Highlight as most popular", type: "checkbox" },
  { name: "is_default", label: "Default plan for new sign-ups", type: "checkbox" },
];

export default function AdminPlans() {
  const { data, reload } = useFetch("/admin/plans");
  const [edit, setEdit] = useState(null);
  const del = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try { await api.delete(`/admin/plans/${p.id}`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="admin-plans-page">
      <PageHeader eyebrow="Pricing" title="Subscription plans" subtitle="Shown on the pricing page and billing page. Paid plans are charged monthly through Stripe."
        actions={<Button onClick={() => setEdit({})} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="add-plan-button"><Plus className="w-4 h-4 mr-1" />New plan</Button>} />
      {!data ? <Loading /> : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
          {data.map((p) => (
            <div key={p.id} className="panel" data-testid={`admin-plan-${p.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-2xl font-bold">{p.name}</h3>
                  <div className="font-display text-xl font-extrabold text-emerald-700 mt-1">{p.price_monthly > 0 ? `${money(p.price_monthly, p.currency)}/mo` : "Free"}</div>
                </div>
                <div className="flex flex-col gap-1 items-end">
                  <StatusBadge status={p.active ? "active" : "skipped"} label={p.active ? "Active" : "Hidden"} />
                  {p.is_default && <StatusBadge status="scheduled" label="Default" />}
                </div>
              </div>
              <p className="text-sm text-slate-500 mt-2">{p.description}</p>
              <p className="text-sm mt-2">{p.max_properties ? `Up to ${p.max_properties} properties` : "Unlimited properties"}</p>
              <ul className="text-sm text-slate-600 mt-3 list-disc pl-5 space-y-1">{p.features.map((f) => <li key={f}>{f}</li>)}</ul>
              <div className="flex gap-2 mt-5">
                <Button size="sm" variant="outline" onClick={() => setEdit({ ...p, features: p.features.join("\n") })} data-testid={`edit-plan-${p.id}`}><Pencil className="w-4 h-4 mr-1" />Edit</Button>
                <Button size="sm" variant="outline" onClick={() => del(p)} data-testid={`delete-plan-${p.id}`}><Trash2 className="w-4 h-4 mr-1 text-rose-600" />Delete</Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <FormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title={edit?.id ? "Edit plan" : "New plan"} testid="plan-form"
        fields={FIELDS} initial={edit}
        onSubmit={async (v) => {
          const body = { ...v, features: String(v.features || "").split("\n").map((s) => s.trim()).filter(Boolean) };
          if (edit.id) await api.put(`/admin/plans/${edit.id}`, body); else await api.post("/admin/plans", body);
          toast.success("Plan saved"); reload();
        }} />
    </div>
  );
}
